import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import AdminDataTable from "../../components/AdminDataTable";
import {
  AdminRowActionsMenu,
  buildActionsColumn,
  useAdminRowActionsMenu,
} from "../../components/AdminRowActions";
import { clearAdminSession, createBanner, fetchBanners, updateBanner } from "../../../lib/api";
import { convertToWebP } from "../../lib/imageUtils";

function emptyForm() {
  return { title: "", mediaFile: null, previewUrl: "", mediaType: null };
}

function rowIsActive(row) {
  return row.active !== false;
}

function MediaPreview({ url, mediaType, className }) {
  if (!url) {
    return <span className={`${className} adm-banner-thumb--empty`}>—</span>;
  }
  if (mediaType === "VIDEO") {
    return <video className={className} src={url} muted playsInline preload="metadata" />;
  }
  return (
    <img
      className={className}
      src={url}
      alt=""
      loading="lazy"
      onError={(e) => {
        e.target.style.display = "none";
      }}
    />
  );
}

export default function AdminHeroBanners() {
  const navigate = useNavigate();
  const [records, setRecords] = useState([]);
  const [allRecords, setAllRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [listError, setListError] = useState("");

  const [filter, setFilter] = useState("all");
  const [formMode, setFormMode] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [mediaPickError, setMediaPickError] = useState("");
  const [converting, setConverting] = useState(false);
  const [toast, setToast] = useState(null);
  const toastTimer = useRef(null);

  const showToast = (msg, type = "warning") => {
    clearTimeout(toastTimer.current);
    setToast({ msg, type });
    toastTimer.current = setTimeout(() => setToast(null), 1000);
  };
  const reloadingRef = useRef(false);
  const hasErrorRef = useRef(false);
  const navigateRef = useRef(navigate);
  const replaceFileInputRef = useRef(null);
  const previewObjectUrlRef = useRef(null);
  const rowActions = useAdminRowActionsMenu();

  useEffect(() => { navigateRef.current = navigate; }, [navigate]);

  const revokePreviewUrl = () => {
    if (previewObjectUrlRef.current) {
      URL.revokeObjectURL(previewObjectUrlRef.current);
      previewObjectUrlRef.current = null;
    }
  };

  const reload = useCallback(async () => {
    if (reloadingRef.current || hasErrorRef.current) return;
    reloadingRef.current = true;
    setLoading(true);
    setListError("");
    try {
      const isActiveParam = filter === "active" ? true : filter === "inactive" ? false : undefined;
      const requests = [fetchBanners()];
      if (isActiveParam !== undefined) requests.push(fetchBanners(isActiveParam));
      const results = await Promise.all(requests);
      const all = results[0].data || [];
      setAllRecords(all);
      setRecords(isActiveParam === undefined ? all : results[1].data || []);
    } catch (err) {
      hasErrorRef.current = true;
      setListError(err.message || "Could not load banners.");
      if (err.status === 401) {
        clearAdminSession();
        navigateRef.current("/admin/login", { replace: true });
      }
    } finally {
      setLoading(false);
      reloadingRef.current = false;
    }
  }, [filter]); // navigate removed — accessed via navigateRef to prevent loop

  useEffect(() => {
    hasErrorRef.current = false;
    reload();
  }, [filter]); // depend on filter directly, not reload reference

  const filtered = useMemo(() => {
    if (filter === "active") return records.filter((r) => rowIsActive(r));
    if (filter === "inactive") return records.filter((r) => !rowIsActive(r));
    return records;
  }, [records, filter]);

  const sortedRows = useMemo(() => {
    return [...filtered].sort((a, b) => (Number(a.sortOrder) || 0) - (Number(b.sortOrder) || 0));
  }, [filtered]);

  const counts = useMemo(() => {
    const active = allRecords.filter((r) => rowIsActive(r)).length;
    return { all: allRecords.length, active, inactive: allRecords.length - active };
  }, [allRecords]);

  const onChange = (name, value) => {
    setForm((f) => ({ ...f, [name]: value }));
  };

  const onMediaFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;

    const isImage = file.type.startsWith("image/");
    const isVideo = file.type.startsWith("video/");
    if (!isImage && !isVideo) {
      setMediaPickError("Please choose an image or video file.");
      return;
    }

    setMediaPickError("");
    let finalFile = file;

    if (isImage) {
      setConverting(true);
      try {
        finalFile = await convertToWebP(file);
      } catch (err) {
        setMediaPickError(err.message || "Image conversion failed. Please try again.");
        setConverting(false);
        return;
      } finally {
        setConverting(false);
      }
    }

    revokePreviewUrl();
    const objectUrl = URL.createObjectURL(finalFile);
    previewObjectUrlRef.current = objectUrl;
    const mediaType = isVideo ? "VIDEO" : "IMAGE";
    setForm((f) => ({ ...f, mediaFile: finalFile, previewUrl: objectUrl, mediaType }));
  };

  const clearMedia = () => {
    if (formMode !== "create") return;
    revokePreviewUrl();
    setMediaPickError("");
    setForm((f) => ({ ...f, mediaFile: null, previewUrl: "", mediaType: null }));
  };

  const openCreate = () => {
    revokePreviewUrl();
    setMediaPickError("");
    setForm(emptyForm());
    setEditingId(null);
    setFormMode("create");
  };

  const openEdit = (row, mode = "edit") => {
    revokePreviewUrl();
    setMediaPickError("");
    setForm({
      title: row.title ?? "",
      mediaFile: null,
      previewUrl: row.mediaUrl ?? "",
      mediaType: row.mediaType ?? null,
    });
    setEditingId(row.id);
    setFormMode(mode);
  };

  const openView = (row) => openEdit(row, "view");

  const closeForm = useCallback(() => {
    revokePreviewUrl();
    setMediaPickError("");
    setFormMode(null);
    setEditingId(null);
    setForm(emptyForm());
  }, []);

  useEffect(() => {
    if (formMode) rowActions.setActionMenu(null);
  }, [formMode, rowActions]);

  useEffect(() => {
    if (!formMode) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") closeForm();
    };
    window.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [formMode, closeForm]);

  useEffect(() => () => revokePreviewUrl(), []);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!String(form.title ?? "").trim()) return;

    const hasMedia = Boolean(form.mediaFile) || Boolean(form.previewUrl);
    if (!hasMedia) {
      setMediaPickError("Please upload a banner image or video.");
      return;
    }

    setSaving(true);
    setMediaPickError("");
    try {
      const title = String(form.title).trim();
      if (formMode === "edit" && editingId != null) {
        await updateBanner(editingId, {
          title,
          file: form.mediaFile || undefined,
        });
      } else {
        if (!form.mediaFile) {
          setMediaPickError("Please upload a banner image or video.");
          setSaving(false);
          return;
        }
        await createBanner({ title, file: form.mediaFile });
      }
      closeForm();
      await reload();
    } catch (err) {
      setMediaPickError(err.message || "Could not save banner.");
      if (err.status === 401) navigate("/admin/login", { replace: true });
    } finally {
      setSaving(false);
    }
  };

  const setBannerActive = async (id, active) => {
    try {
      await updateBanner(id, { active });
      await reload();
    } catch (err) {
      if (err.status === 401) { navigate("/admin/login", { replace: true }); return; }
      showToast(err.message || "Could not update status.");
    }
  };

  const isCreate = formMode === "create";
  const isView = formMode === "view";
  const showRemoveMedia = isCreate && Boolean(form.previewUrl);
  const previewSrc = form.previewUrl;

  const bannerColumns = useMemo(
    () => [
    {
      id: "preview",
      header: "Preview",
      width: "11%",
      render: (row) => (
        <MediaPreview url={row.mediaUrl} mediaType={row.mediaType} className="adm-banner-thumb" />
      ),
    },
    {
      id: "title",
      header: "Title",
      width: "51%",
      tdClassName: "adm-data-cell-name",
      render: (row) => <strong>{row.title || "—"}</strong>,
    },
    {
      id: "status",
      header: "Status",
      width: "14%",
      render: (row) => (
        <span className={`adm-status-pill${rowIsActive(row) ? " adm-status-pill--active" : " adm-status-pill--inactive"}`}>
          {rowIsActive(row) ? "Active" : "Inactive"}
        </span>
      ),
    },
    buildActionsColumn({
      rowActions,
      getRowLabel: (row) => row.title || "banner",
    }),
  ],
    [rowActions]
  );

  return (
    <div className="adm-page adm-page--wide adm-page--banners">
      <header className="adm-page-head adm-page-head--row">
        <h1 className="adm-page-title">Home — Banners Master</h1>
        <button type="button" className="adm-btn adm-btn--primary" onClick={openCreate}>
          Add banner
        </button>
      </header>

      {listError ? (
        <div className="adm-empty-state" role="alert">
          <svg width="48" height="48" viewBox="0 0 48 48" fill="none" style={{ color: "#ef4444" }}>
            <circle cx="24" cy="24" r="20" stroke="currentColor" strokeWidth="2" />
            <path d="M24 14v12M24 32v2" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
          </svg>
          <p className="adm-empty-state__title">Failed to load banners</p>
          <p className="adm-empty-state__sub">{listError}</p>
          <button
            type="button"
            className="adm-btn adm-btn--ghost"
            onClick={() => { hasErrorRef.current = false; reload(); }}
          >
            Try again
          </button>
        </div>
      ) : null}

      <div className="adm-banner-toolbar adm-banner-toolbar--filters-only">
        <div className="adm-banner-toolbar-left" role="tablist" aria-label="Filter banners by status">
          <button
            type="button"
            role="tab"
            aria-selected={filter === "all"}
            className={`adm-filter-tab${filter === "all" ? " adm-filter-tab--active" : ""}`}
            onClick={() => setFilter("all")}
          >
            All ({counts.all})
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={filter === "active"}
            className={`adm-filter-tab${filter === "active" ? " adm-filter-tab--active" : ""}`}
            onClick={() => setFilter("active")}
          >
            Active ({counts.active})
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={filter === "inactive"}
            className={`adm-filter-tab${filter === "inactive" ? " adm-filter-tab--active" : ""}`}
            onClick={() => setFilter("inactive")}
          >
            Inactive ({counts.inactive})
          </button>
        </div>
      </div>

      {formMode &&
        createPortal(
          <div className="adm-drawer-root">
            <button type="button" className="adm-drawer-backdrop" aria-label="Close panel" onClick={closeForm} />
            <aside
              className="adm-drawer-panel"
              role="dialog"
              aria-modal="true"
              aria-labelledby="adm-banner-drawer-title"
            >
              <div className="adm-drawer-panel-inner">
                <header className="adm-drawer-header">
                  <h2 id="adm-banner-drawer-title" className="adm-drawer-title">
                    {isView ? "View banner" : formMode === "edit" ? "Update banner" : "Add banner"}
                  </h2>
                  <button type="button" className="adm-drawer-close" onClick={closeForm} aria-label="Close">
                    <span aria-hidden>×</span>
                  </button>
                </header>
                <form id="adm-banner-drawer-form" className="adm-drawer-form" onSubmit={onSubmit}>
                  <div className="adm-drawer-fields">
                    <label className="adm-field">
                      <span className="adm-field-label">
                        Title<span className="adm-req"> *</span>
                      </span>
                      <input
                        className="adm-input"
                        value={form.title}
                        onChange={(e) => onChange("title", e.target.value)}
                        placeholder="Banner title"
                        autoFocus={!isView}
                        readOnly={isView}
                        disabled={isView}
                      />
                    </label>

                    <div className="adm-field">
                      <span className="adm-field-label">
                        Image / video<span className="adm-req"> *</span>
                      </span>
                      <p className="adm-upload-hint">
                        Images are automatically converted to WebP and compressed to under 5 MB. Videos are uploaded as-is.
                      </p>
                      {converting && (
                        <p className="adm-upload-hint" style={{ color: "#2563eb", fontWeight: 500 }}>
                          Converting image to WebP…
                        </p>
                      )}

                      {previewSrc ? (
                        <div className="adm-banner-picker-preview">
                          <MediaPreview url={previewSrc} mediaType={form.mediaType} className="adm-banner-picker-img" />
                          {!isView ? (
                            <div className="adm-banner-picker-actions">
                              <button
                                type="button"
                                className="adm-btn adm-btn--ghost adm-btn--sm"
                                onClick={() => replaceFileInputRef.current?.click()}
                              >
                                Change file
                              </button>
                              <input
                                ref={replaceFileInputRef}
                                type="file"
                                accept="image/*,video/*"
                                className="adm-visually-hidden"
                                onChange={onMediaFile}
                              />
                              {showRemoveMedia ? (
                                <button type="button" className="adm-btn adm-btn--ghost adm-btn--sm" onClick={clearMedia}>
                                  Remove file
                                </button>
                              ) : null}
                            </div>
                          ) : null}
                        </div>
                      ) : !isView ? (
                        <label className="adm-file-picker">
                          <input type="file" accept="image/*,video/*" onChange={onMediaFile} />
                          <span className="adm-file-picker-label">Choose image or video…</span>
                          <span className="adm-file-picker-hint">Images & videos</span>
                        </label>
                      ) : null}

                      {mediaPickError ? (
                        <p className="adm-field-error" role="alert">
                          {mediaPickError}
                        </p>
                      ) : null}
                    </div>
                  </div>
                  <footer className="adm-drawer-footer">
                    <button type="button" className="adm-btn adm-btn--ghost" onClick={closeForm} disabled={saving}>
                      {isView ? "Close" : "Cancel"}
                    </button>
                    {!isView ? (
                      <button type="submit" className="adm-btn adm-btn--primary" disabled={saving || converting}>
                        {converting ? "Converting…" : saving ? "Saving…" : formMode === "edit" ? "Save changes" : "Save banner"}
                      </button>
                    ) : null}
                  </footer>
                </form>
              </div>
            </aside>
          </div>,
          document.body
        )}

      <AdminRowActionsMenu
        actionMenu={rowActions.actionMenu}
        onClose={rowActions.close}
        ariaLabel="Banner actions"
        isActive={rowActions.actionMenu ? rowIsActive(rowActions.actionMenu.row) : false}
        onView={(row) => openView(row)}
        onUpdate={(row) => openEdit(row)}
        onToggleActive={(row) => setBannerActive(row.id, !rowIsActive(row))}
      />

      {toast && <div className={`adm-toast adm-toast--${toast.type}`}>{toast.msg}</div>}

      <section className="adm-card adm-card--table">
        {loading ? (
          <div className="adm-empty-state">
            <svg width="40" height="40" viewBox="0 0 40 40" fill="none" style={{ color: "#94a3b8" }}>
              <circle cx="20" cy="20" r="16" stroke="currentColor" strokeWidth="2" strokeDasharray="6 4" />
            </svg>
            <p className="adm-empty-state__title">Loading banners…</p>
          </div>
        ) : listError ? null : (
          <AdminDataTable
            layout="fixed"
            columns={bannerColumns}
            rows={sortedRows}
            getRowKey={(r) => r.id}
            emptyState={
              allRecords.length === 0 ? (
                <div className="adm-empty-state adm-empty-state--inline">
                  <svg width="40" height="40" viewBox="0 0 40 40" fill="none" style={{ color: "#94a3b8" }}>
                    <rect x="8" y="10" width="24" height="20" rx="3" stroke="currentColor" strokeWidth="1.8" />
                    <path d="M14 18h12M14 23h8" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                    <path d="M20 6v4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                  </svg>
                  <p className="adm-empty-state__title">No banners found</p>
                  <p className="adm-empty-state__sub">Click &quot;Add banner&quot; to create your first one.</p>
                </div>
              ) : (
                <div className="adm-empty-state adm-empty-state--inline">
                  <svg width="40" height="40" viewBox="0 0 40 40" fill="none" style={{ color: "#94a3b8" }}>
                    <circle cx="18" cy="18" r="10" stroke="currentColor" strokeWidth="1.8" />
                    <path d="M26 26l6 6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                    <path d="M14 18h8M18 14v8" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                  </svg>
                  <p className="adm-empty-state__title">No banners match this filter</p>
                  <p className="adm-empty-state__sub">Try switching to "All" to see everything.</p>
                </div>
              )
            }
          />
        )}
      </section>
    </div>
  );
}
